/**
 * Axios HTTP Client Configuration
 * Centralized axios instance with interceptors and error handling
 * Updated to use Descope authentication tokens
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { getSessionToken } from '@descope/react-sdk';
import { API_ENDPOINTS, API_CONFIG, HTTP_STATUS } from 'config/api.config';
import { getUserFriendlyErrorMessage } from 'utils/retryUtils';

/**
 * Custom error interface for API errors
 */
export interface ApiError extends Error {
  status?: number;
  data?: any;
  isRetryable?: boolean;
  userMessage?: string;
}

/**
 * Create axios instance with default configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_ENDPOINTS.BASE,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * Add Descope authentication token to all requests
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    try {
      // Get current session token from Descope
      // This always fetches a fresh token, handling refresh automatically
      const token: string | undefined = await getSessionToken();

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('[apiClient] ✅ Bearer token attached to request:', config.url);
      } else {
        console.warn('[apiClient] ⚠️ No token available for request:', config.url);
      }
    } catch (error) {
      // If token fetch fails, log but continue with request
      // (request will likely be rejected by backend if auth is required)
      console.error('[apiClient] ❌ Failed to get Descope session token:', error);
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handle errors globally with retry logic
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  async (error: AxiosError): Promise<any> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // Initialize retry count
    if (originalRequest._retryCount === undefined) {
      originalRequest._retryCount = 0;
    }

    // Check if we should retry this error
    const shouldRetry = !error.response || [408, 429, 500, 502, 503, 504].includes(error.response.status);
    const maxRetries = API_CONFIG.RETRY.MAX_RETRIES;
    const canRetry = originalRequest._retryCount < maxRetries && shouldRetry;

    // Handle retry logic for network/server errors
    if (canRetry && originalRequest) {
      originalRequest._retryCount++;

      // Calculate delay with exponential backoff
      const delay = API_CONFIG.RETRY.RETRY_DELAY * Math.pow(API_CONFIG.RETRY.BACKOFF_MULTIPLIER, originalRequest._retryCount - 1);

      console.log(
        `Retrying request (attempt ${originalRequest._retryCount}/${maxRetries + 1}):`,
        originalRequest.url,
        `after ${delay}ms`
      );

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request
      return apiClient(originalRequest);
    }

    // Handle 401 Unauthorized - Session expired (only once)
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Descope handles token refresh automatically
        // Try to get a fresh token
        const newToken: string | undefined = await getSessionToken();

        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } else {
          // No valid session, redirect to login
          window.location.href = '/login';
        }
      } catch (refreshError) {
        // Redirect to login if refresh fails
        console.error('Token refresh failed:', refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Enhance error with user-friendly message
    const enhancedError = error as ApiError;
    enhancedError.userMessage = getUserFriendlyErrorMessage(error);

    // Log the error for debugging
    console.error('API Error:', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      message: enhancedError.userMessage,
      originalError: error.message,
      retryCount: originalRequest._retryCount,
    });

    // Handle other errors
    return Promise.reject(enhancedError);
  }
);

/**
 * Create axios instance for file uploads with extended timeout
 */
export const uploadClient: AxiosInstance = axios.create({
  baseURL: API_ENDPOINTS.BASE,
  timeout: API_CONFIG.UPLOAD_TIMEOUT,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

/**
 * Upload Client Request Interceptor
 * Add Descope authentication token to all upload requests
 */
uploadClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    try {
      // Get current session token from Descope
      const token: string | undefined = await getSessionToken();

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('[uploadClient] ✅ Bearer token attached to upload request:', config.url);
      } else {
        console.warn('[uploadClient] ⚠️ No token available for upload request:', config.url);
      }
    } catch (error) {
      console.error('[uploadClient] ❌ Failed to get Descope session token for upload:', error);
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
);

/**
 * Upload Client Response Interceptor
 * Handle errors globally with retry logic
 */
uploadClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<any> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // Initialize retry count
    if (originalRequest._retryCount === undefined) {
      originalRequest._retryCount = 0;
    }

    // Check if we should retry this error (be more conservative with uploads)
    const shouldRetry = !error.response || [408, 500, 502, 503, 504].includes(error.response.status);
    const maxRetries = API_CONFIG.RETRY.MAX_RETRIES;
    const canRetry = originalRequest._retryCount < maxRetries && shouldRetry;

    // Handle retry logic for network/server errors
    if (canRetry && originalRequest) {
      originalRequest._retryCount++;

      // Calculate delay with exponential backoff
      const delay = API_CONFIG.RETRY.RETRY_DELAY * Math.pow(API_CONFIG.RETRY.BACKOFF_MULTIPLIER, originalRequest._retryCount - 1);

      console.log(
        `Retrying upload request (attempt ${originalRequest._retryCount}/${maxRetries + 1}):`,
        originalRequest.url,
        `after ${delay}ms`
      );

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request
      return uploadClient(originalRequest);
    }

    // Handle 401 Unauthorized for uploads (only once)
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken: string | undefined = await getSessionToken();

        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return uploadClient(originalRequest);
        } else {
          window.location.href = '/login';
        }
      } catch (refreshError) {
        console.error('Token refresh failed for upload:', refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Enhance error with user-friendly message
    const enhancedError = error as ApiError;
    enhancedError.userMessage = getUserFriendlyErrorMessage(error);

    // Log the error for debugging
    console.error('Upload API Error:', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      message: enhancedError.userMessage,
      originalError: error.message,
      retryCount: originalRequest._retryCount,
    });

    return Promise.reject(enhancedError);
  }
);

export default apiClient;
